import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TPA_RRE } from '../../../models/tpa-rre-management.model';

export interface TpaRreManagementModalData {
  isEdit: boolean;
  record?: TPA_RRE;
}

@Component({
  selector: 'app-tpa-rre-management-modal',
  standalone: false,
  templateUrl: './tpa-rre-management-modal.component.html',
  styleUrl: './tpa-rre-management-modal.component.css'
})
export class TpaRreManagementModalComponent {
  readonly reportingPeriods = Array.from({ length: 12 }, (_, i) => i + 1);
  readonly form;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<TpaRreManagementModalComponent, TPA_RRE>,
    @Inject(MAT_DIALOG_DATA) public data: TpaRreManagementModalData
  ) {
    const reportingPeriod = Number.parseInt(String(data.record?.ReportingGroup ?? '1'), 10);
    const safeReportingPeriod = Number.isNaN(reportingPeriod) ? 1 : reportingPeriod;

    this.form = this.fb.nonNullable.group({
      rre: [{ value: data.record?.Rre ?? '', disabled: data.isEdit }, [Validators.required, Validators.pattern(/^\d{9}$/)]],
      rreName: [data.record?.RreName ?? '', [Validators.required, Validators.maxLength(70)]],
      reportingGroup: [safeReportingPeriod],
      responseMonitoringOn: [!!data.record?.ResponseMonitoringOn],
      submissionAgent: [data.record?.SubmissionAgent ?? '', [Validators.maxLength(100)]],
      productionDate: [data.record?.ProductionDate ?? null],
      isocpAccountDesigneeOnly: [!!data.record?.IsocpAccountDesigneeOnly],
      comment: [data.record?.Comment ?? '', [Validators.maxLength(250)]],
    });
  }

  onRreInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const digitsOnly = input.value.replace(/\D/g, '').slice(0, 9);
    if (digitsOnly !== input.value) {
      this.form.controls.rre.setValue(digitsOnly);
    }
  }

  onRreBlur(): void {
    if (this.data.isEdit) {
      return;
    }

    const paddedValue = this.normalizeRreForAdd(this.form.controls.rre.value);
    this.form.controls.rre.setValue(paddedValue);
  }

  get showRreError(): boolean {
    const control = this.form.controls.rre;
    return control.invalid && (control.dirty || control.touched);
  }

  onSubmit(): void {
    if (!this.data.isEdit) {
      const paddedValue = this.normalizeRreForAdd(this.form.controls.rre.value);
      this.form.controls.rre.setValue(paddedValue);
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const payload: TPA_RRE = {
      ...(this.data.record ?? {
        LastUpdatedBy: '',
        LastUpdated: new Date(),
      }),
      Rre: this.data.isEdit ? this.data.record?.Rre ?? '' : value.rre,
      RreName: value.rreName,
      ReportingGroup: String(value.reportingGroup),
      ResponseMonitoringOn: value.responseMonitoringOn,
      SubmissionAgent: value.submissionAgent,
      ProductionDate: value.productionDate ?? new Date(),
      IsocpAccountDesigneeOnly: value.isocpAccountDesigneeOnly,
      Comment: value.comment,
      LastUpdated: new Date(),
    };

    this.dialogRef.close(payload);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private normalizeRreForAdd(value: string): string {
    const digitsOnly = String(value ?? '').replace(/\D/g, '').slice(0, 9);
    if (!digitsOnly) {
      return '';
    }

    return digitsOnly.padStart(9, '0');
  }
}
